import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  Assignment as FormIcon,
  AccountTree as FlowIcon,
  Chat as ChatIcon,
  People as PeopleIcon,
  Schedule as ScheduleIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  BarChart as ChartIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function Analytics({ darkMode }) {
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('7days');
  const [selectedForm, setSelectedForm] = useState('all');
  const [forms, setForms] = useState([]);
  const [flows, setFlows] = useState([]);

  const [analytics, setAnalytics] = useState({
    overview: {
      totalForms: 0,
      totalSubmissions: 0,
      totalChats: 0,
      totalFlows: 0,
      avgResponseTime: 0,
      completionRate: 0,
    },
    formAnalytics: [],
    flowAnalytics: [],
    chatAnalytics: {
      totalMessages: 0,
      uniqueUsers: 0,
      avgSessionLength: 0,
      popularModels: [],
    },
    recentActivity: [],
  });

  useEffect(() => {
    loadAnalyticsData();
    loadForms();
    loadFlows();
  }, [dateRange, selectedForm]);

  const loadForms = async () => {
    try {
      const response = await fetch('/api/v1/forms/designs', {
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('tkn')}` },
      });

      if (response.ok) {
        const data = await response.json();
        setForms(data.forms || []);
      }
    } catch (error) {
      console.error('Error loading forms:', error);
    }
  };

  const loadFlows = async () => {
    try {
      const response = await fetch('/api/v1/flows', {
        credentials: 'include',
        headers: { Authorization: `Bearer ${localStorage.getItem('tkn')}` },
      });

      if (response.ok) {
        const data = await response.json();
        setFlows(data.flows || []);
      }
    } catch (error) {
      console.error('Error loading flows:', error);
    }
  };

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // Mock analytics data - replace with actual API calls
      const mockAnalytics = {
        overview: {
          totalForms: forms.length || 12,
          totalSubmissions: 1547,
          totalChats: 8924,
          totalFlows: flows.length || 8,
          avgResponseTime: 2.4,
          completionRate: 87.3,
        },
        formAnalytics: [
          {
            id: '1',
            name: 'Contact Form',
            submissions: 324,
            views: 1205,
            conversionRate: 26.9,
            avgTime: 185,
            status: 'active',
          },
          {
            id: '2',
            name: 'Feedback Survey',
            submissions: 189,
            views: 567,
            conversionRate: 33.3,
            avgTime: 124,
            status: 'active',
          },
          {
            id: '3',
            name: 'Registration Form',
            submissions: 456,
            views: 892,
            conversionRate: 51.1,
            avgTime: 203,
            status: 'active',
          },
        ],
        flowAnalytics: [
          {
            id: '1',
            name: 'Customer Support Flow',
            executions: 245,
            successRate: 92.3,
            avgDuration: 45,
            status: 'deployed',
          },
          {
            id: '2',
            name: 'Data Processing Flow',
            executions: 156,
            successRate: 88.5,
            avgDuration: 123,
            status: 'deployed',
          },
        ],
        chatAnalytics: {
          totalMessages: 8924,
          uniqueUsers: 1247,
          avgSessionLength: 12.5,
          popularModels: [
            { name: 'LLaMA 4', usage: 65.2 },
            { name: 'LLaMA 3', usage: 24.1 },
            { name: 'Mixtral', usage: 8.9 },
            { name: 'Code LLaMA', usage: 1.8 },
          ],
        },
        recentActivity: [
          {
            type: 'form_submission',
            title: 'New form submission received',
            description: 'Contact Form received a new submission',
            timestamp: new Date(Date.now() - 1000 * 60 * 15),
          },
          {
            type: 'flow_execution',
            title: 'Agentic flow completed',
            description: 'Customer Support Flow executed successfully',
            timestamp: new Date(Date.now() - 1000 * 60 * 45),
          },
          {
            type: 'chat_session',
            title: 'High chat activity',
            description: '156 new chat messages in the last hour',
            timestamp: new Date(Date.now() - 1000 * 60 * 60),
          },
        ],
      };

      setAnalytics(mockAnalytics);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportAnalytics = () => {
    const exportData = {
      dateRange,
      selectedForm,
      analytics,
      exportedAt: new Date().toISOString(),
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-${dateRange}-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'deployed': return 'success';
      case 'inactive': return 'error';
      case 'draft': return 'warning';
      default: return 'default';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'form_submission': return <FormIcon color="primary" />;
      case 'flow_execution': return <FlowIcon color="secondary" />;
      case 'chat_session': return <ChatIcon color="info" />;
      default: return <AnalyticsIcon />;
    }
  };

  const tabContent = [
    // Overview Tab
    <Box>
      {/* Key Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <FormIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4">{analytics.overview.totalForms}</Typography>
              <Typography color="text.secondary" variant="body2">Total Forms</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUpIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
              <Typography variant="h4">{analytics.overview.totalSubmissions}</Typography>
              <Typography color="text.secondary" variant="body2">Submissions</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ChatIcon sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
              <Typography variant="h4">{analytics.overview.totalChats}</Typography>
              <Typography color="text.secondary" variant="body2">Chat Messages</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <FlowIcon sx={{ fontSize: 32, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h4">{analytics.overview.totalFlows}</Typography>
              <Typography color="text.secondary" variant="body2">Active Flows</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <ScheduleIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4">
                {analytics.overview.avgResponseTime}
                s
              </Typography>
              <Typography color="text.secondary" variant="body2">Avg Response</Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PeopleIcon sx={{ fontSize: 32, color: 'error.main', mb: 1 }} />
              <Typography variant="h4">
                {analytics.overview.completionRate}
                %
              </Typography>
              <Typography color="text.secondary" variant="body2">Completion Rate</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Recent Activity</Typography>
        <List>
          {analytics.recentActivity.map((activity, index) => (
            <ListItem key={index} divider={index < analytics.recentActivity.length - 1}>
              <ListItemIcon>
                {getActivityIcon(activity.type)}
              </ListItemIcon>
              <ListItemText
                primary={activity.title}
                secondary={(
                  <Box>
                    <Typography variant="body2">{activity.description}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {activity.timestamp.toLocaleString()}
                    </Typography>
                  </Box>
                )}
              />
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>,

    // Forms Analytics Tab
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Form Name</TableCell>
              <TableCell align="right">Submissions</TableCell>
              <TableCell align="right">Views</TableCell>
              <TableCell align="right">Conversion Rate</TableCell>
              <TableCell align="right">Avg Time (s)</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {analytics.formAnalytics.map((form) => (
              <TableRow key={form.id}>
                <TableCell>{form.name}</TableCell>
                <TableCell align="right">{form.submissions}</TableCell>
                <TableCell align="right">{form.views}</TableCell>
                <TableCell align="right">
                  {form.conversionRate}
                  %
                </TableCell>
                <TableCell align="right">{form.avgTime}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={form.status}
                    color={getStatusColor(form.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="View Details">
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/form-analytics/${form.id}`)}
                    >
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>,

    // Flows Analytics Tab
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Flow Name</TableCell>
              <TableCell align="right">Executions</TableCell>
              <TableCell align="right">Success Rate</TableCell>
              <TableCell align="right">Avg Duration (s)</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {analytics.flowAnalytics.map((flow) => (
              <TableRow key={flow.id}>
                <TableCell>{flow.name}</TableCell>
                <TableCell align="right">{flow.executions}</TableCell>
                <TableCell align="right">
                  {flow.successRate}
                  %
                </TableCell>
                <TableCell align="right">{flow.avgDuration}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={flow.status}
                    color={getStatusColor(flow.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="View Details">
                    <IconButton size="small">
                      <ViewIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>,

    // Chat Analytics Tab
    <Box>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3 }}>Chat Statistics</Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <ChatIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                    <Typography variant="h4">{analytics.chatAnalytics.totalMessages}</Typography>
                    <Typography color="text.secondary">Total Messages</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <PeopleIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h4">{analytics.chatAnalytics.uniqueUsers}</Typography>
                    <Typography color="text.secondary">Unique Users</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center' }}>
                    <ScheduleIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                    <Typography variant="h4">
                      {analytics.chatAnalytics.avgSessionLength}
                      m
                    </Typography>
                    <Typography color="text.secondary">Avg Session</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Popular Models</Typography>
            {analytics.chatAnalytics.popularModels.map((model, index) => (
              <Box key={index} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="body2">{model.name}</Typography>
                  <Typography variant="body2">
                    {model.usage}
                    %
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={model.usage}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Box>,
  ];

  return (
    <Box sx={{
      p: 3, bgcolor: 'background.default', minHeight: '100vh', height: '100%', overflow: 'auto',
    }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2,
        }}
        >
          <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AnalyticsIcon />
            {' '}
            Analytics
          </Typography>

          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Date Range</InputLabel>
              <Select
                value={dateRange}
                label="Date Range"
                onChange={(e) => setDateRange(e.target.value)}
              >
                <MenuItem value="1day">Last 24 hours</MenuItem>
                <MenuItem value="7days">Last 7 days</MenuItem>
                <MenuItem value="30days">Last 30 days</MenuItem>
                <MenuItem value="90days">Last 90 days</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Form</InputLabel>
              <Select
                value={selectedForm}
                label="Form"
                onChange={(e) => setSelectedForm(e.target.value)}
              >
                <MenuItem value="all">All Forms</MenuItem>
                {forms.map((form) => (
                  <MenuItem key={form._id} value={form._id}>
                    {form.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadAnalyticsData}
              disabled={loading}
            >
              Refresh
            </Button>

            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={exportAnalytics}
            >
              Export
            </Button>
          </Stack>
        </Box>

        <Typography variant="body1" color="text.secondary">
          Comprehensive analytics for your forms, flows, and chat interactions.
        </Typography>
      </Box>

      {/* Tab Navigation */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant="fullWidth"
        >
          <Tab icon={<ChartIcon />} label="Overview" />
          <Tab icon={<FormIcon />} label="Forms" />
          <Tab icon={<FlowIcon />} label="Flows" />
          <Tab icon={<ChatIcon />} label="Chat" />
        </Tabs>
      </Paper>

      {/* Loading */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Tab Content */}
      <Box>
        {tabContent[selectedTab]}
      </Box>
    </Box>
  );
}

export default Analytics;
